import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  UNSAFE_DataRouterContext,
  useBlocker,
  useLocation,
  type BlockerFunction,
} from 'react-router-dom';
import { afterOverlayHistorySettles, useBackToClose } from '@/hooks/useBackToClose';
import { useHoldUpdates } from '@/pwa/busy';
import '@/components/session.css';

/**
 * The unsaved-changes guard (#343).
 *
 * Settings cards with an explicit Save used to lose their edits in silence:
 * toggle "Calendar reminders", go to Mail, come back, and the change was gone.
 * This is the one guard for all of them.
 *
 * ## Shape
 *
 * - A card says whether it is dirty with {@link useUnsavedChangesGuard}. Dirty
 *   means "differs from what was last saved"; a switch that saves on its own
 *   is never dirty. The hook also holds `beforeunload` while dirty, for the
 *   exits the app does not own (closing the tab, reload, a typed URL).
 * - {@link UnsavedChangesProvider}, once, above every routed view, owns the
 *   router's blocker. React Router supports ONE blocker at a time, so a
 *   blocker per card would be a blocker that silently does nothing on every
 *   page with two cards; the provider holds one and asks the registry.
 * - When a navigation that would unmount a dirty card is held, one dialog
 *   names every dirty card: Stay keeps the page and the edits, Leave goes.
 *
 * Only a change of PATH is held. A search-param or state-only change leaves
 * the route (and the card) mounted, so there is nothing to lose.
 *
 * ## Phone Back and overlays
 *
 * `useBackToClose` swallows the pop that closes an overlay before the router
 * hears it, so Back closing a sheet over a dirty page never reaches this
 * blocker. Back that leaves the page does, and is held like a link.
 *
 * The dialog is itself an overlay (Back = Stay), with two ordering rules:
 *
 * - After a blocked Back, the router puts the URL back with `history.go(1)`.
 *   The dialog's own history entry must not be pushed until that restore has
 *   landed, or the restore would be read as Back closing the dialog.
 * - Leave closes the dialog first (its entry comes off with `history.back()`)
 *   and only lets the router move once that pop has been delivered.
 */

interface Registry {
  set: (id: string, label: string | null) => void;
}

const UnsavedChangesContext = createContext<Registry | null>(null);

/**
 * Marks the calling card dirty while `dirty` is true.
 *
 * `label` names it in the leave dialog ("You have unsaved changes in
 * Notifications"), so it should be the card's heading as the user sees it.
 *
 * Works without a provider (component tests, a future host): then only the
 * `beforeunload` half is active.
 */
export function useUnsavedChangesGuard(dirty: boolean, label = 'this page'): void {
  const registry = useContext(UnsavedChangesContext);
  const id = useId();
  // Unsaved edits also keep a new version from being applied by a silent
  // reload; it is offered instead (src/pwa/updates.ts).
  useHoldUpdates(dirty, label);

  useEffect(() => {
    if (!registry || !dirty) return;
    registry.set(id, label);
    return () => registry.set(id, null);
  }, [registry, id, dirty, label]);

  useEffect(() => {
    if (!dirty) return;
    // The browser owns this dialog and its wording: `returnValue` text has
    // not been shown for years. Setting it is still what older engines need.
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
}

/** Mount once, inside the data router, above every view with a guarded card. */
export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  // The ref is the truth, read by the blocker at navigation time; the state
  // copy is what the dialog renders.
  const entriesRef = useRef<Map<string, string>>(new Map());
  const [labels, setLabels] = useState<string[]>([]);

  const registry = useMemo<Registry>(
    () => ({
      set: (id, label) => {
        const entries = entriesRef.current;
        if (label === null ? !entries.has(id) : entries.get(id) === label) return;
        if (label === null) entries.delete(id);
        else entries.set(id, label);
        setLabels([...entries.values()]);
      },
    }),
    [],
  );

  // `useBlocker` throws without a data router; the component that calls it is
  // mounted only where there is one.
  const hasDataRouter = useContext(UNSAFE_DataRouterContext) !== null;

  return (
    <UnsavedChangesContext.Provider value={registry}>
      {children}
      {hasDataRouter ? <LeaveBlocker entriesRef={entriesRef} labels={labels} /> : null}
    </UnsavedChangesContext.Provider>
  );
}

function LeaveBlocker({
  entriesRef,
  labels,
}: {
  entriesRef: { current: ReadonlyMap<string, string> };
  labels: string[];
}) {
  const [fromBack, setFromBack] = useState(false);
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation, historyAction }) => {
      // Never in the way of /login: that is a sign-out or a revoked session,
      // already torn down by the time the router hears of it, so there is no
      // session left to save into and Stay would strand the user on a dead one.
      const block =
        entriesRef.current.size > 0 &&
        currentLocation.pathname !== nextLocation.pathname &&
        nextLocation.pathname !== '/login';
      if (block) setFromBack(historyAction === 'POP');
      return block;
    },
    [entriesRef],
  );
  const blocker = useBlocker(shouldBlock);
  // The navigation Leave was pressed for, by its location key.
  const [leavingFor, setLeavingFor] = useState<string | null>(null);

  const blocked = blocker.state === 'blocked' ? blocker : null;
  const leaving = blocked !== null && leavingFor === blocked.location.key;

  // Leave: the dialog is gone by the time this runs (its cleanup, which takes
  // its history entry back off, runs before this effect), so wait for that
  // pop and then let the router go.
  const proceed = leaving ? blocked.proceed : undefined;
  useEffect(() => {
    if (proceed) afterOverlayHistorySettles(proceed);
  }, [proceed]);

  if (blocked === null || leaving) return null;

  // Unique, in registration order: two cards with the same heading are one
  // line to the reader.
  const unique = [...new Set(labels)];
  return (
    <UnsavedChangesDialog
      labels={unique}
      fromBack={fromBack}
      onStay={() => blocked.reset()}
      onLeave={() => setLeavingFor(blocked.location.key)}
    />
  );
}

/** "Notifications", "General and Signature", "A, B and C". */
function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? 'this page';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function routerKeyOf(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const value = (state as Record<string, unknown>).key;
  return typeof value === 'string' ? value : undefined;
}

/**
 * Waits for the router's URL restore after a blocked Back.
 *
 * Settled once the browser's current entry is the router's current location
 * again. A memory router (tests) never touches `window.history`, so a timer
 * stands in for the pop that will not come.
 */
function useRestoreSettled(fromBack: boolean): boolean {
  const { key } = useLocation();
  const [settled, setSettled] = useState(
    () => !fromBack || routerKeyOf(window.history.state) === key,
  );
  useEffect(() => {
    if (settled) return;
    const check = () => {
      if (routerKeyOf(window.history.state) === key) setSettled(true);
    };
    window.addEventListener('popstate', check);
    const fallback = window.setTimeout(() => setSettled(true), 600);
    return () => {
      window.removeEventListener('popstate', check);
      window.clearTimeout(fallback);
    };
  }, [settled, key]);
  return settled;
}

function UnsavedChangesDialog({
  labels,
  fromBack,
  onStay,
  onLeave,
}: {
  labels: string[];
  fromBack: boolean;
  onStay: () => void;
  onLeave: () => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const stayRef = useRef<HTMLButtonElement | null>(null);
  const settled = useRestoreSettled(fromBack);
  // A phone's Back while the question is open is Stay, like Escape.
  useBackToClose(settled, onStay);

  useEffect(() => {
    stayRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onStay();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onStay]);

  const many = labels.length > 1;
  return (
    <div className="reauth__scrim reauth__scrim--top">
      <section
        className="reauth__dialog card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <h2 className="reauth__title" id={titleId}>
          Leave without saving?
        </h2>
        {many ? (
          <div id={descriptionId} className="reauth__body">
            <p className="reauth__body">You have unsaved changes in:</p>
            <ul className="unsaved-dialog__list">
              {labels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="reauth__body" id={descriptionId}>
            You have unsaved changes in {joinLabels(labels)}. Leave without saving?
          </p>
        )}
        <div className="reauth__actions">
          <button ref={stayRef} type="button" className="btn btn--primary" onClick={onStay}>
            Stay
          </button>
          <button type="button" className="btn btn--danger-solid" onClick={onLeave}>
            Leave
          </button>
        </div>
      </section>
    </div>
  );
}
